/**
 * FounderTimeline — single source of truth for a founder's lifecycle.
 *
 * Renders the 8-phase vertical timeline:
 *   applied → ai_council → human_decision → onboarded →
 *   ai_qa → psychometric → nda_signed → mou_active
 *
 * Used by BOTH:
 *   /dashboard/founder           (founder reads their own timeline)
 *   /dashboard/founders/:userId  (admin reads any founder's timeline)
 *
 * The component decides which inline actions to render based on `isAdmin`
 * + the active phase. No per-phase action mutates source data here — they
 * all dispatch to existing endpoints (/api/vetting/{id}/decide,
 * /api/startups/qa/restart, /api/startups/psycho, etc.) so the timeline
 * is read-only-ish — it composes existing flows rather than re-implementing.
 */
import React, { useEffect, useState, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  CheckCircle2, Clock, Lock, XCircle, AlertTriangle, FileText, Brain,
  Compass, FileSignature, Sparkles, Send, MailQuestion, Rocket, Upload,
  Download, RefreshCw, ExternalLink, ChevronRight,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import LinkedInButton from '@/components/LinkedInButton';

// Phase metadata — id → icon + colour family.  The icon is purely
// decorative; the actual status pill drives the colour.
const PHASE_META = {
  applied:        { icon: Send,          desc: 'Application submitted via /founder-apply.' },
  ai_council:     { icon: Sparkles,      desc: '3 AI personas reviewed the pitch deck + summary.' },
  human_decision: { icon: ShieldIcon,    desc: 'Admin issued approve / improve / meeting / reject.' },
  onboarded:      { icon: Rocket,        desc: 'Workspace access granted. Founder lands in /dashboard.' },
  ai_qa:          { icon: Brain,         desc: 'Strict gate: score ≥ 65 AND verdict = approve.' },
  psychometric:   { icon: Compass,       desc: 'Big-5 OCEAN report + council overlay decision signal.' },
  nda_signed:     { icon: FileSignature, desc: 'Mutual NDA with the holding company signed.' },
  mou_active:     { icon: FileSignature, desc: 'MOU active — founder is live in the portfolio.' },
};
function ShieldIcon(props) {
  return <CheckCircle2 {...props}/>;
}

function StatusPill({ status }) {
  const map = {
    completed: { c: 'bg-emerald-50 text-emerald-700 border-emerald-200', l: 'Complete' },
    active:    { c: 'bg-[#FFF8E5] text-[#5E1F8B] border-[#8B3FC5]', l: 'Active · action here' },
    blocked:   { c: 'bg-rose-50 text-rose-700 border-rose-200', l: 'Blocked' },
    open:      { c: 'bg-amber-50 text-amber-700 border-amber-200', l: 'Open' },
    locked:    { c: 'bg-slate-50 text-slate-400 border-slate-200', l: 'Locked' },
  };
  const m = map[status] || map.locked;
  return (
    <span
      data-testid={`phase-status-${status}`}
      className={`text-[9px] uppercase tracking-[0.22em] font-bold px-2 py-1 rounded border ${m.c}`}
    >
      {m.l}
    </span>
  );
}

function fmtDate(d) {
  if (!d) return '—';
  try { return new Date(d).toLocaleString(); } catch { return String(d); }
}

// ───────────── per-phase content blocks ─────────────

function PhaseAppliedDetail({ p }) {
  if (p.status !== 'completed') return null;
  return (
    <div className="mt-3 space-y-2 text-xs">
      <div><span className="text-slate-500">Startup: </span><b>{p.startup_name}</b> · <span className="text-slate-500">{p.industry}</span></div>
      {p.pitch_deck_url && (
        <a href={p.pitch_deck_url} target="_blank" rel="noreferrer"
           className="text-[#5E1F8B] hover:text-[#0A0A0A] inline-flex items-center gap-1 font-bold"
           data-testid="phase-applied-deck-link">
          <ExternalLink size={12}/> Pitch deck
        </a>
      )}
      {p.pitch_summary && (
        <details className="text-slate-600 mt-2">
          <summary className="cursor-pointer text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500">
            Pitch summary
          </summary>
          <p className="mt-1.5 leading-relaxed italic">{p.pitch_summary}</p>
        </details>
      )}
    </div>
  );
}

function PhaseAICouncilDetail({ p }) {
  if (p.status === 'locked') return null;
  if (p.status === 'active') {
    return (
      <div className="mt-3 text-xs text-slate-600 flex items-center gap-2 italic">
        <Clock size={12} className="animate-pulse"/> AI council is deliberating · polling for results.
      </div>
    );
  }
  return (
    <div className="mt-3 space-y-3 text-xs">
      <div className="flex items-center gap-3">
        <div className="ve-card px-3 py-1.5">
          <div className="text-[9px] uppercase tracking-[0.22em] text-slate-500 font-bold">AI Score</div>
          <div className="display text-lg text-[#8B3FC5] font-bold">{Math.round(p.ai_score || 0)}</div>
        </div>
        <div className={`text-[10px] uppercase tracking-[0.22em] font-bold px-2 py-1 rounded border ${
          p.ai_verdict === 'approve' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
          p.ai_verdict === 'reject' ? 'bg-rose-50 text-rose-700 border-rose-200' :
          'bg-amber-50 text-amber-700 border-amber-200'
        }`}>
          {p.ai_verdict || 'review'}
        </div>
      </div>
      {p.ai_strengths?.length > 0 && (
        <div>
          <div className="text-[9px] uppercase tracking-[0.18em] font-bold text-emerald-700">Strengths</div>
          <ul className="mt-1 space-y-0.5">
            {p.ai_strengths.slice(0, 3).map((s, i) => (
              <li key={i} className="flex gap-1.5"><CheckCircle2 size={11} className="text-emerald-600 mt-0.5 shrink-0"/> {s}</li>
            ))}
          </ul>
        </div>
      )}
      {p.ai_concerns?.length > 0 && (
        <div>
          <div className="text-[9px] uppercase tracking-[0.18em] font-bold text-rose-700">Concerns</div>
          <ul className="mt-1 space-y-0.5">
            {p.ai_concerns.slice(0, 3).map((s, i) => (
              <li key={i} className="flex gap-1.5"><AlertTriangle size={11} className="text-rose-600 mt-0.5 shrink-0"/> {s}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function PhaseHumanDecisionDetail({ p, assessmentId, isAdmin, onReload }) {
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');
  const [meetingUrl, setMeetingUrl] = useState('');

  if (p.status === 'locked') return null;

  // Decision rendered (history)
  if (p.status === 'completed' || p.status === 'blocked') {
    const tone = p.decision === 'approve' ? 'emerald' :
      p.decision === 'reject' ? 'rose' : 'amber';
    return (
      <div className={`mt-3 text-xs border-l-2 pl-3 border-${tone}-300`}>
        <div className="flex items-center gap-2">
          <span className={`text-[10px] uppercase tracking-[0.22em] font-bold text-${tone}-700`}>
            {p.decision}
          </span>
          {p.timestamp && <span className="text-slate-500">· {fmtDate(p.timestamp)}</span>}
        </div>
        {p.note && (
          <p className="mt-1.5 text-slate-700 italic">&quot;{p.note}&quot;</p>
        )}
        {p.meeting_url && (
          <a href={p.meeting_url} target="_blank" rel="noreferrer"
             className="text-[#5E1F8B] hover:text-[#0A0A0A] inline-flex items-center gap-1 mt-2">
            <ExternalLink size={11}/> Meeting link
          </a>
        )}
      </div>
    );
  }

  // active — admin can act inline
  if (!isAdmin) {
    return (
      <div className="mt-3 text-xs text-slate-600 italic flex items-center gap-2">
        <Clock size={12}/> Awaiting reviewer decision.
      </div>
    );
  }

  const decide = async (decision) => {
    if ((decision === 'reject' || decision === 'meeting') && !note.trim()) {
      toast.error(`A note is required for ${decision}.`);
      return;
    }
    setBusy(true);
    try {
      await api.post(`/vetting/${assessmentId}/decide`, {
        decision, note: note.trim() || null, meeting_url: meetingUrl.trim() || null,
      });
      toast.success(`Decision recorded — ${decision}.`);
      setNote(''); setMeetingUrl('');
      onReload && onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Decision failed.');
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-3 space-y-3">
      <div className="text-[9px] uppercase tracking-[0.22em] text-slate-500 font-bold">Decision note</div>
      <textarea
        value={note} onChange={(e) => setNote(e.target.value)} rows={2}
        placeholder="Reasoning (required for reject / meeting)…"
        data-testid="phase-decision-note"
        className="w-full text-xs rounded-md border border-slate-200 px-3 py-2 focus:outline-none focus:border-[#8B3FC5]"
      />
      <input
        value={meetingUrl} onChange={(e) => setMeetingUrl(e.target.value)}
        placeholder="Meeting URL (only for 'meeting')"
        data-testid="phase-decision-meeting-url"
        className="w-full text-xs rounded-md border border-slate-200 px-3 py-2 focus:outline-none focus:border-[#8B3FC5]"
      />
      <div className="flex flex-wrap gap-2">
        <button onClick={() => decide('approve')} disabled={busy}
                data-testid="phase-decide-approve"
                className="text-xs font-bold uppercase tracking-[0.1em] px-3 py-1.5 rounded border border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100 disabled:opacity-50">
          ✓ Approve
        </button>
        <button onClick={() => decide('improve')} disabled={busy}
                data-testid="phase-decide-improve"
                className="text-xs font-bold uppercase tracking-[0.1em] px-3 py-1.5 rounded border border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100 disabled:opacity-50">
          ↻ Improve
        </button>
        <button onClick={() => decide('meeting')} disabled={busy}
                data-testid="phase-decide-meeting"
                className="text-xs font-bold uppercase tracking-[0.1em] px-3 py-1.5 rounded border border-sky-300 bg-sky-50 text-sky-800 hover:bg-sky-100 disabled:opacity-50">
          ✉ Meeting
        </button>
        <button onClick={() => decide('reject')} disabled={busy}
                data-testid="phase-decide-reject"
                className="text-xs font-bold uppercase tracking-[0.1em] px-3 py-1.5 rounded border border-rose-300 bg-rose-50 text-rose-800 hover:bg-rose-100 disabled:opacity-50">
          ✕ Reject
        </button>
      </div>
    </div>
  );
}

function PhaseAIQADetail({ p, startupId, isAdmin, onReload }) {
  const [busy, setBusy] = useState(false);
  if (p.status === 'locked') return null;
  const handleRestart = async () => {
    if (!startupId) return;
    setBusy(true);
    try {
      await api.post('/startups/qa/restart', { startup_id: startupId });
      toast.success('Fresh AI Q&A session unlocked.');
      onReload && onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Restart failed');
    } finally { setBusy(false); }
  };
  if (p.status === 'completed') {
    return (
      <div className="mt-3 text-xs text-slate-700">
        <span className="text-emerald-700 font-bold">✓ Passed</span>
        {p.final_score != null && (
          <span className="ml-3 text-slate-500">Score: <b className="text-slate-900">{Math.round(p.final_score)}</b></span>
        )}
        {p.final_verdict && (
          <span className="ml-2 text-slate-500 capitalize">· {p.final_verdict}</span>
        )}
      </div>
    );
  }
  if (p.status === 'blocked') {
    return (
      <div className="mt-3 text-xs space-y-2">
        <div className="text-rose-700">
          <b>Gate failed</b> · score {Math.round(p.final_score || 0)} · verdict {p.final_verdict || 'review'}
        </div>
        <div className="text-slate-600">
          Attempts left: <b>{p.qa_attempts_left ?? 0}</b> of 3
          {p.qa_locked && <span className="text-rose-700 ml-1">· locked, contact reviewer</span>}
        </div>
        {p.qa_attempts_left > 0 && !p.qa_locked && startupId && (
          <button onClick={handleRestart} disabled={busy}
                  data-testid="phase-qa-restart"
                  className="ve-btn-secondary text-xs">
            <RefreshCw size={11}/> {busy ? 'Restarting…' : 'Retry the Q&A'}
          </button>
        )}
        {isAdmin && (
          <Link to={`/dashboard/founder-vetting?tab=qa_review`}
                className="text-[#5E1F8B] hover:text-[#0A0A0A] text-xs font-bold uppercase tracking-[0.1em] ml-2">
            Override → Q&A queue
          </Link>
        )}
      </div>
    );
  }
  // active
  return (
    <div className="mt-3 text-xs space-y-2">
      <p className="text-slate-600">Strict gate: score ≥ 65 AND verdict = approve. Up to 3 attempts.</p>
      {startupId && (
        <Link to={`/dashboard/startups/${startupId}?tab=qa`}
              data-testid="phase-qa-start"
              className="ve-btn-secondary text-xs inline-flex">
          <Brain size={11}/> Start the AI interview
        </Link>
      )}
    </div>
  );
}

function PhasePsychometricDetail({ p, startupId }) {
  if (p.status === 'locked') return null;
  if (p.status === 'completed') {
    const o = p.council_overlay;
    return (
      <div className="mt-3 space-y-3 text-xs">
        <div className="flex gap-3 flex-wrap">
          <div className="ve-card px-3 py-1.5">
            <div className="text-[9px] uppercase tracking-[0.22em] text-slate-500 font-bold">Leadership</div>
            <div className="display text-lg text-[#8B3FC5] font-bold">{Math.round(p.leadership_index || 0)}</div>
          </div>
          <div className="ve-card px-3 py-1.5">
            <div className="text-[9px] uppercase tracking-[0.22em] text-slate-500 font-bold">Risk appetite</div>
            <div className="display text-lg text-[#8B3FC5] font-bold">{Math.round(p.risk_appetite || 0)}</div>
          </div>
          {o && (
            <div className={`ve-card px-3 py-1.5 border ${
              o.verdict === 'green' ? 'border-emerald-300 bg-emerald-50' :
              o.verdict === 'amber' ? 'border-amber-300 bg-amber-50' :
              'border-rose-300 bg-rose-50'
            }`} title={`AI ${o.components.ai_score} × 60% + Lead ${o.components.leadership_index} × 25% + Risk-align ${o.components.risk_alignment} × 15%`}>
              <div className="text-[9px] uppercase tracking-[0.22em] text-slate-600 font-bold">Council overlay</div>
              <div className="display text-lg font-bold">{o.decision_signal}</div>
            </div>
          )}
        </div>
        {p.summary && <p className="text-slate-600 italic">&quot;{p.summary}&quot;</p>}
        {p.flags?.length > 0 && (
          <div className="text-rose-700 text-xs">⚠ {p.flags.join(' · ')}</div>
        )}
        {p.report_id && (
          <Link
            to={`/dashboard/psycho-reports/${p.report_id}`}
            data-testid="phase-psycho-view-report"
            className="ve-btn-secondary text-xs inline-flex"
          >
            <FileText size={11}/> View full report
          </Link>
        )}
      </div>
    );
  }
  // active
  return (
    <div className="mt-3 text-xs space-y-2">
      <p className="text-slate-600">12 questions · 20 seconds each · auto-scored. Take it once — the report is saved to your profile.</p>
      {startupId && (
        <Link to={`/dashboard/startups/${startupId}/psycho-quiz`}
              data-testid="phase-psycho-start"
              className="ve-btn-secondary text-xs inline-flex">
          <Compass size={11}/> Take psychometric (20s/Q)
        </Link>
      )}
    </div>
  );
}

function PhaseAgreementDetail({ p, kind }) {
  if (p.status === 'locked') return null;
  if (p.status === 'completed') {
    return (
      <div className="mt-3 text-xs text-emerald-700">
        ✓ Signed
        {p.timestamp && <span className="text-slate-500 ml-2">· {fmtDate(p.timestamp)}</span>}
        {p.partnership_type && <span className="text-slate-500 ml-2 capitalize">· {p.partnership_type}</span>}
      </div>
    );
  }
  return (
    <div className="mt-3 text-xs">
      <Link to="/dashboard/agreements"
            data-testid={`phase-${kind}-open`}
            className="ve-btn-secondary inline-flex">
        <FileSignature size={11}/> Open agreements
      </Link>
    </div>
  );
}

function PhaseOnboardedDetail({ p }) {
  if (p.status === 'locked') return null;
  if (p.status === 'completed') {
    return (
      <div className="mt-3 text-xs text-emerald-700">
        ✓ Workspace access granted.
        {p.startup_id && (
          <Link to={`/dashboard/startups/${p.startup_id}`}
                data-testid="phase-onboarded-startup-link"
                className="ml-2 text-[#5E1F8B] hover:text-[#0A0A0A] inline-flex items-center gap-1 font-bold">
            View startup <ChevronRight size={11}/>
          </Link>
        )}
      </div>
    );
  }
  if (p.status === 'blocked') {
    return (
      <div className="mt-3 text-xs text-rose-700 italic">{p.note}</div>
    );
  }
  // active (improve / meeting)
  return (
    <div className="mt-3 text-xs">
      <div className="text-amber-800 italic">{p.note}</div>
      {p.meeting_url && (
        <a href={p.meeting_url} target="_blank" rel="noreferrer"
           className="mt-1.5 text-[#5E1F8B] hover:text-[#0A0A0A] inline-flex items-center gap-1 font-bold">
          <ExternalLink size={11}/> Meeting link
        </a>
      )}
    </div>
  );
}

// ───────────── main component ─────────────

/* eslint-disable -- v6 react-hooks/set-state-in-effect false-positive on the polling effect. */
export default function FounderTimeline() {
  const { userId } = useParams();
  const { user } = useAuth();
  const [t, setT] = useState(null);
  const [loading, setLoading] = useState(true);
  const targetUserId = userId || user?.user_id;
  const isAdmin = user?.role === 'system_admin' || user?.role === 'holding_admin';

  const load = async () => {
    if (!targetUserId) return;
    try {
      const url = userId
        ? `/founder-timeline/${userId}`
        : '/founder-timeline/me';
      const { data } = await api.get(url);
      setT(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load timeline.');
    } finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    // Auto-poll while there's an active or in-progress phase so AI council
    // results, q&a completions and admin decisions surface live.
    const id = setInterval(load, 8000);
    return () => clearInterval(id);
  }, [targetUserId]);

  if (loading) return <div className="text-sm text-slate-500">Loading the founder journey…</div>;
  if (!t) return null;

  const assessmentId = t.phases.find((p) => p.id === 'applied')?.assessment_id;
  const startupId = t.startup?.startup_id;

  return (
    <div data-testid="founder-timeline">
      {/* Header — founder identity + progress ring */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="ve-eyebrow">{isAdmin && userId ? 'Founder Timeline · Admin view' : 'Your founder journey'}</div>
          <h1 className="display text-3xl md:text-4xl mt-2 font-bold flex flex-wrap items-center gap-3">
            <span>{t.founder.name || 'Founder'}</span>
            <LinkedInButton url={t.founder.linkedin_url} size={18}/>
          </h1>
          {t.startup?.name && (
            <h2 className="display ve-gold-text text-2xl md:text-3xl mt-1 flex items-center gap-2">
              {t.startup.name}
              <LinkedInButton url={t.startup.linkedin_url} size={14} label="Startup LinkedIn"/>
            </h2>
          )}
          <p className="text-sm text-slate-600 mt-2 max-w-2xl">
            {t.founder.email}{t.founder.company ? ` · ${t.founder.company}` : ''}
            {t.startup?.industry && ` · ${t.startup.industry}`}
          </p>
        </div>
        <div className="flex items-center gap-4">
          <div className="relative h-20 w-20 shrink-0">
            <svg className="absolute inset-0" viewBox="0 0 80 80">
              <circle cx="40" cy="40" r="34" fill="none" stroke="#E5E7EB" strokeWidth="6"/>
              <circle cx="40" cy="40" r="34" fill="none" stroke="#8B3FC5" strokeWidth="6"
                      strokeDasharray={`${(t.percent / 100) * 213.6} 213.6`}
                      strokeLinecap="round" transform="rotate(-90 40 40)"/>
            </svg>
            <div className="absolute inset-0 flex items-center justify-center font-bold display text-lg">
              {t.percent}%
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold">
              {t.completed} of {t.total} phases
            </div>
            <div className="display text-base font-bold mt-1">
              {t.current_phase_id
                ? `Now: ${t.phases.find((p) => p.id === t.current_phase_id)?.label}`
                : '🎉 All phases complete'}
            </div>
            {t.blocked > 0 && (
              <div className="text-rose-700 text-xs mt-1">{t.blocked} blocked</div>
            )}
          </div>
        </div>
      </div>

      {/* Vertical timeline */}
      <div className="mt-8 relative">
        {/* connector line — gold gradient down the centre */}
        <div className="absolute left-[19px] top-2 bottom-2 w-[2px] bg-gradient-to-b from-[#8B3FC5] via-[#8B3FC5]/40 to-slate-200"/>

        <div className="space-y-3">
          {t.phases.map((p, i) => {
            const meta = PHASE_META[p.id] || {};
            const Icon = meta.icon || CheckCircle2;
            const isLocked = p.status === 'locked';
            const isDone = p.status === 'completed';
            const isBlocked = p.status === 'blocked';
            const isActive = p.status === 'active';
            return (
              <div
                key={p.id}
                data-testid={`timeline-phase-${p.id}`}
                className={`relative pl-12 transition-opacity ${isLocked ? 'opacity-50' : ''}`}
              >
                {/* node circle */}
                <span
                  className={`absolute left-0 top-2 h-10 w-10 rounded-full flex items-center justify-center border-2 z-10 ${
                    isDone ? 'bg-emerald-500 border-emerald-500 text-white' :
                    isBlocked ? 'bg-rose-500 border-rose-500 text-white' :
                    isActive ? 'bg-[#8B3FC5] border-[#8B3FC5] text-white animate-pulse' :
                    isLocked ? 'bg-white border-slate-300 text-slate-400' :
                    'bg-white border-[#8B3FC5] text-[#5E1F8B]'
                  }`}
                >
                  {isLocked ? <Lock size={14}/> :
                   isDone ? <CheckCircle2 size={16}/> :
                   isBlocked ? <XCircle size={16}/> :
                   <Icon size={16}/>}
                </span>

                <div className={`ve-card p-4 ${
                  isActive ? 'border-[#8B3FC5] ring-2 ring-[#8B3FC5]/20' :
                  isBlocked ? 'border-rose-300' : ''
                }`}>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center gap-3">
                      <span className="text-[10px] uppercase tracking-[0.22em] text-slate-400 font-bold">Phase {i + 1}</span>
                      <StatusPill status={p.status}/>
                    </div>
                    {p.timestamp && (
                      <span className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-mono">
                        {fmtDate(p.timestamp)}
                      </span>
                    )}
                  </div>
                  <h3 className="display text-lg font-bold mt-1.5">{p.label}</h3>
                  <p className="text-[11px] text-slate-500 mt-0.5">{meta.desc}</p>

                  {/* Per-phase detail */}
                  {p.id === 'applied' && <PhaseAppliedDetail p={p}/>}
                  {p.id === 'ai_council' && <PhaseAICouncilDetail p={p}/>}
                  {p.id === 'human_decision' && (
                    <PhaseHumanDecisionDetail
                      p={p}
                      assessmentId={assessmentId}
                      isAdmin={isAdmin}
                      onReload={load}
                    />
                  )}
                  {p.id === 'onboarded' && <PhaseOnboardedDetail p={p}/>}
                  {p.id === 'ai_qa' && (
                    <PhaseAIQADetail p={p} startupId={startupId} isAdmin={isAdmin} onReload={load}/>
                  )}
                  {p.id === 'psychometric' && (
                    <PhasePsychometricDetail p={p} startupId={startupId}/>
                  )}
                  {p.id === 'nda_signed' && <PhaseAgreementDetail p={p} kind="nda"/>}
                  {p.id === 'mou_active' && <PhaseAgreementDetail p={p} kind="mou"/>}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Footer cross-links */}
      <div className="mt-8 ve-card p-5 border-[#8B3FC5] bg-gradient-to-r from-[#FFF8E5] to-white text-sm">
        {isAdmin ? (
          <div className="flex flex-wrap items-center justify-between gap-3">
            <span>
              <Link to="/dashboard/founders" className="text-[#5E1F8B] font-bold hover:text-[#0A0A0A]">
                ← All founders
              </Link>
            </span>
            {startupId && (
              <Link to={`/dashboard/startups/${startupId}`}
                    className="text-[#5E1F8B] font-bold hover:text-[#0A0A0A]">
                Open startup workspace →
              </Link>
            )}
          </div>
        ) : (
          <div>
            <div className="ve-eyebrow">Stuck on a phase?</div>
            <p className="text-slate-700 mt-1">
              Email <a href="mailto:founders@vigilantentities.com" className="text-[#5E1F8B] font-bold">founders@vigilantentities.com</a> or open a compliance chat. We respond within 1 working day.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
